import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { CheckCircleIcon, DocumentTextIcon, XIcon, ExclamationCircleIcon } from '@heroicons/react/outline';

// Native apply (FEAT-APPLY phase 4). A modal with name, email, phone, cover letter and résumé,
// sent to the CanadaJobLeads submitApplication function. Form only (D-016): no mailto fallback.
// Limits mirror the server: résumé PDF/DOC/DOCX ≤ 4 MB, cover letter ≤ 5,000 characters.

const MAX_BYTES = 4 * 1024 * 1024;
const MAX_LETTER = 5000;
const EXTENSIONS = ['pdf', 'doc', 'docx'];
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let turnstileLoading = null;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstileLoading) {
    turnstileLoading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = () => {
        turnstileLoading = null;
        reject(new Error('turnstile-load-failed'));
      };
      document.head.appendChild(s);
    });
  }
  return turnstileLoading;
}

function readBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

function formatSize(bytes) {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (e) {
    return '';
  }
}

export function appliedOn(jobId) {
  try {
    return window.localStorage.getItem(`applied:${jobId}`);
  } catch (e) {
    return null;
  }
}

function rememberApplied(jobId, iso) {
  try {
    window.localStorage.setItem(`applied:${jobId}`, iso);
  } catch (e) {
    // storage unavailable: the confirmation still shows
  }
}

// Candidate details remembered on this device for the next application (name, email, phone only;
// never the cover letter or résumé). Opt-out checkbox and a "Clear" link for shared computers.
const SAVED_KEY = 'cjl:applicant';
function loadSaved() {
  try {
    const v = JSON.parse(window.localStorage.getItem(SAVED_KEY) || 'null');
    return v && typeof v === 'object' ? v : null;
  } catch (e) {
    return null;
  }
}
function saveDetails(details) {
  try {
    window.localStorage.setItem(SAVED_KEY, JSON.stringify(details));
  } catch (e) {
    // storage unavailable: nothing to remember
  }
}
function forgetDetails() {
  try {
    window.localStorage.removeItem(SAVED_KEY);
  } catch (e) {
    // ignore
  }
}

// One checkbox style for the modal: a fixed 16px box in the board's brand colour (the boards have no
// forms plugin, and native checkbox colouring varies by browser), pointer cursor, visible focus ring.
function Checkbox({ checked, onChange, required = false }) {
  return (
    <span className="relative mr-2 mt-0.5 inline-flex h-4 w-4 flex-shrink-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        required={required}
        className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-gray-400 bg-white checked:border-primary checked:bg-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
      />
      <svg viewBox="0 0 16 16" className="pointer-events-none absolute inset-0 hidden h-4 w-4 text-white peer-checked:block" aria-hidden="true">
        <path d="M4 8.5l2.5 2.5L12 5.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

const inputClass =
  'mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

export default function ApplyModal({ open, onClose, job, config, onApplied }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [letter, setLetter] = useState('');
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot
  const [remember, setRemember] = useState(true);
  const [prefilled, setPrefilled] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | duplicate | error
  const [message, setMessage] = useState('');
  const [fileError, setFileError] = useState('');
  const [token, setToken] = useState('');
  const [appliedAt, setAppliedAt] = useState('');
  const widgetRef = useRef(null);
  const widgetId = useRef(null);
  const fileInput = useRef(null);
  const sending = status === 'sending';
  const done = status === 'sent' || status === 'duplicate';

  // Pre-fill from this device when the form opens empty.
  useEffect(() => {
    if (!open || done || name || email || phone) return;
    const saved = loadSaved();
    if (saved && (saved.name || saved.email)) {
      setName(String(saved.name || '').slice(0, 120));
      setEmail(String(saved.email || '').slice(0, 254));
      setPhone(String(saved.phone || '').slice(0, 40));
      setPrefilled(true);
    }
  }, [open]);

  function clearSaved() {
    forgetDetails();
    setName('');
    setEmail('');
    setPhone('');
    setPrefilled(false);
  }

  // Render the Turnstile check while the form is showing (once per opening).
  useEffect(() => {
    if (!open || done) return undefined;
    let cancelled = false;
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !widgetRef.current || widgetId.current !== null) return;
        widgetId.current = ts.render(widgetRef.current, {
          sitekey: config.turnstileSiteKey,
          theme: 'light',
          callback: (t) => setToken(t),
          'expired-callback': () => setToken(''),
          'error-callback': () => setToken(''),
        });
      })
      .catch(() => setMessage('We could not load the security check. Please refresh the page and try again.'));
    return () => {
      cancelled = true;
      if (widgetId.current !== null && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
      setToken('');
    };
  }, [open, done, config.turnstileSiteKey]);

  function resetTurnstile() {
    setToken('');
    if (widgetId.current !== null && window.turnstile) window.turnstile.reset(widgetId.current);
  }

  function pickFile(e) {
    const f = e.target.files && e.target.files[0];
    setFileError('');
    if (!f) return;
    const ext = f.name.toLowerCase().split('.').pop();
    if (!EXTENSIONS.includes(ext)) {
      setFileError('Please choose a PDF or Word document (.pdf, .doc or .docx).');
      e.target.value = '';
      return;
    }
    if (f.size > MAX_BYTES) {
      setFileError(`That file is ${formatSize(f.size)}. Your résumé must be 4 MB or smaller.`);
      e.target.value = '';
      return;
    }
    setFile(f);
  }

  function removeFile() {
    setFile(null);
    if (fileInput.current) fileInput.current.value = '';
  }

  function close() {
    if (status === 'sending') return;
    onClose();
    if (done) {
      // A finished application starts fresh next time (e.g. applying with a different email).
      setStatus('idle');
      setMessage('');
      setName('');
      setEmail('');
      setPhone('');
      setLetter('');
      setFile(null);
      setConsent(false);
      setFileError('');
      setPrefilled(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    setMessage('');
    if (!file) {
      setFileError('Please attach your résumé.');
      return;
    }
    if (!token) {
      setMessage('Please complete the security check above (tick “Verify you are human” if it appears), then submit again.');
      return;
    }
    setStatus('sending');
    try {
      const data = await readBase64(file);
      const res = await fetch(config.api, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          jobId: job._id,
          name,
          email,
          phone,
          coverLetter: letter,
          consent,
          website,
          turnstileToken: token,
          resume: { name: file.name, data },
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        if (remember) saveDetails({ name: name.trim(), email: email.trim(), phone: phone.trim() });
        else forgetDetails();
        const at = body.appliedAt || new Date().toISOString();
        rememberApplied(job._id, at);
        setAppliedAt(at);
        setStatus('sent');
        if (onApplied) onApplied(at);
        return;
      }
      if (res.status === 409) {
        const at = body.appliedAt || '';
        if (at) rememberApplied(job._id, at);
        setAppliedAt(at);
        setStatus('duplicate');
        if (onApplied && at) onApplied(at);
        return;
      }
      setStatus('error');
      setMessage(body.error || 'Something went wrong. Please try again.');
      resetTurnstile();
    } catch (err) {
      setStatus('error');
      setMessage('We could not send your application. Please check your connection and try again.');
      resetTurnstile();
    }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-40 overflow-y-auto" onClose={close}>
        <div className="flex min-h-screen items-end justify-center px-2 pb-2 text-center sm:items-center sm:px-4 sm:pb-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-40" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-4 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="relative my-4 inline-block w-full max-w-lg transform rounded-lg bg-white text-left align-middle shadow-xl">
              <button
                type="button"
                onClick={close}
                className="absolute right-3 top-3 rounded p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label="Close"
              >
                <XIcon className="h-5 w-5" aria-hidden="true" />
              </button>

              {done ? (
                <div className="px-6 py-10 text-center">
                  <CheckCircleIcon className="mx-auto h-12 w-12 text-green-600" aria-hidden="true" />
                  <Dialog.Title className="mt-4 text-xl font-bold font-[Poppins] text-gray-900">
                    {status === 'sent' ? 'Application submitted' : "You've already applied"}
                  </Dialog.Title>
                  <p className="mt-2 text-sm text-gray-600">
                    {status === 'sent'
                      ? `Your application for ${job.title} has been sent to ${job.companyName || 'the employer'}. They'll contact you directly if they'd like to move forward.`
                      : `You applied for ${job.title}${appliedAt ? ` on ${formatDate(appliedAt)}` : ''}. The employer already has your application.`}
                  </p>
                  <button
                    type="button"
                    onClick={close}
                    className="mt-6 inline-flex justify-center rounded-md border border-primary bg-primary px-5 py-2 text-sm font-semibold text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  >
                    Done
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate={false} className="px-5 pb-5 pt-6 sm:px-6">
                  <Dialog.Title className="pr-8 text-lg font-bold font-[Poppins] text-gray-900">Apply for {job.title}</Dialog.Title>
                  {job.companyName && <p className="mt-0.5 text-sm text-gray-500">{job.companyName}</p>}
                  {prefilled && (
                    <p className="mt-3 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
                      We filled in your details from your last application on this device.{' '}
                      <button type="button" onClick={clearSaved} className="font-semibold text-primary hover:underline">
                        Not you? Clear
                      </button>
                    </p>
                  )}

                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    <label className="block text-sm font-medium text-gray-700 sm:col-span-2">
                      Full name <span className="text-red-600">*</span>
                      <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} autoComplete="name" />
                    </label>
                    <label className="block text-sm font-medium text-gray-700">
                      Email <span className="text-red-600">*</span>
                      <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={254} autoComplete="email" />
                    </label>
                    <label className="block text-sm font-medium text-gray-700">
                      Phone <span className="font-normal text-gray-400">(optional)</span>
                      <input className={inputClass} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} autoComplete="tel" />
                    </label>
                  </div>

                  <div className="mt-4">
                    <span className="block text-sm font-medium text-gray-700">
                      Résumé <span className="text-red-600">*</span>
                      <span className="ml-1 font-normal text-gray-400">PDF or Word, up to 4 MB</span>
                    </span>
                    {file ? (
                      <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                        <span className="flex min-w-0 items-center text-sm text-gray-800">
                          <DocumentTextIcon className="mr-2 h-5 w-5 flex-shrink-0 text-primary" aria-hidden="true" />
                          <span className="truncate">{file.name}</span>
                          <span className="ml-2 flex-shrink-0 text-gray-400">{formatSize(file.size)}</span>
                        </span>
                        <button type="button" onClick={removeFile} className="ml-3 text-xs font-semibold text-primary hover:underline">
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="mt-1 flex cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-gray-300 px-3 py-4 text-sm text-gray-600 hover:border-primary focus-within:ring-2 focus-within:ring-primary">
                        <span>
                          <span className="font-semibold text-primary">Choose a file</span> to attach
                        </span>
                        <input
                          ref={fileInput}
                          type="file"
                          className="sr-only"
                          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          onChange={pickFile}
                        />
                      </label>
                    )}
                    {fileError && <p className="mt-1 text-xs text-red-600">{fileError}</p>}
                  </div>

                  <label className="mt-4 block text-sm font-medium text-gray-700">
                    Cover letter <span className="font-normal text-gray-400">(optional)</span>
                    <textarea
                      className={`${inputClass} h-32 resize-y`}
                      value={letter}
                      onChange={(e) => setLetter(e.target.value.slice(0, MAX_LETTER))}
                      placeholder="Type or paste your cover letter"
                    />
                    <span className="mt-1 block text-right text-xs font-normal text-gray-400">
                      {letter.length.toLocaleString()} / {MAX_LETTER.toLocaleString()}
                    </span>
                  </label>

                  {/* Honeypot: hidden from people and screen readers; bots tend to fill it. */}
                  <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                    <label>
                      Website
                      <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
                    </label>
                  </div>

                  <label className="mt-2 flex cursor-pointer items-start text-sm text-gray-600">
                    <Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} required />
                    <span>
                      I agree to share this application with {job.companyName || 'the employer'}, and for it to be kept for up to 6 months after this
                      job closes. See our{' '}
                      <a href="/terms-and-conditions#applications-privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">
                        privacy notice
                      </a>
                      .
                    </span>
                  </label>

                  <label className="mt-2 flex cursor-pointer items-start text-sm text-gray-600">
                    <Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                    <span>Remember my name, email and phone on this device</span>
                  </label>

                  <div className="mt-4" ref={widgetRef} />

                  {message && (
                    <p className="mt-3 flex items-start rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                      <ExclamationCircleIcon className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                      {message}
                    </p>
                  )}

                  <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                      type="button"
                      onClick={close}
                      disabled={sending}
                      className="inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={sending}
                      className="inline-flex justify-center rounded-md border border-primary bg-primary px-5 py-2 text-sm font-semibold text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-60"
                    >
                      {sending ? 'Sending…' : 'Submit application'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
